/** Deep links INTO the /subscriptions grid from other features (e.g. the
 *  tour-operator integrations dialog), and the quick actions every value in
 *  the subscription detail drawer offers.
 *
 * The tenant scope travels as the page's `?tenant=<id>` picker param (the
 * picker is authoritative for tenant and strips tenant chips from `?q=`);
 * restech / account become equality chips. Operators are resolved through
 * the namespaces config — never hardcoded — so the link can't drift from the
 * backend contract (the namespaces themselves are contract-tested).
 *
 * The drawer's cross-grid hops: a delivery timestamp opens the webhook log
 * around that moment (/api-requests is windowed by `apiRequestsHref`), the
 * bound credential opens `/api-credentials/<id>` and filters that grid by its
 * prefix / fingerprint, and the fingerprint comes back the other way as
 * `/subscriptions?q=credential=<fp>`.
 */
import {
  equalityOperator,
  serialize,
  toWireAst,
  type FilterNode,
  type WireNode,
} from '@/components/QuerySearch';
import { act, type ValueAction } from '@/components/ValueActions/ValueActions';
import {
  apiCredentialsHref,
  apiRequestsHref,
  cond,
  createdAroundNode,
  subscriptionsHref,
  tourOperatorsHref,
} from '@/utils/gridLinks';

import { buildSubscriptionNamespaces } from './subscriptionNamespaces';

function conditionNode(namespace: string, value: string): FilterNode | null {
  const cfg = buildSubscriptionNamespaces()[namespace];
  const operator = cfg ? equalityOperator(cfg.operators) : null;
  if (!operator) return null;
  return { kind: 'cond', namespace, operator, value };
}

/** /subscriptions scoped to one tenant + restech (+ account slug when the
 *  row isn't the default account — the default account has no slug to
 *  filter by, and tenant+restech already pins it down). */
export function subscriptionsHrefForAccount(
  toId: number,
  restech: string,
  slug?: string | null,
): string {
  const nodes = [
    conditionNode('restech', restech),
    ...(slug ? [conditionNode('account', slug)] : []),
  ].filter((n): n is FilterNode => n !== null);
  const usp = new URLSearchParams();
  usp.set('tenant', String(toId));
  const q = serialize(nodes);
  if (q) usp.set('q', q);
  return `/subscriptions?${usp.toString()}`;
}

/** Wire-AST `filters` for one integration ACCOUNT's subscriptions, for direct
 *  API calls (the integration dialog's Subscriptions tab). Unlike the href
 *  above this always sends an `account` leaf: the backend compiles the
 *  literal `default` to the empty-slug default account. */
export function subscriptionAccountWireFilters(restech: string, slug?: string | null): WireNode[] {
  const nodes = [
    conditionNode('restech', restech),
    conditionNode('account', slug || 'default'),
  ].filter((n): n is FilterNode => n !== null);
  return toWireAst(nodes);
}

// ---- Drawer value actions -------------------------------------------------

export interface SubscriptionCtx {
  /** Scopes the grid links (`?tenant=`). */
  tenantId: number | null | undefined;
  /** The subscription's restech codename — the integration every webhook of
   *  this subscription is logged under. */
  restech: string;
}

const tenantOf = (ctx: SubscriptionCtx) => ctx.tenantId ?? null;

export function tenantActions(tenant: { id: number; parent_id?: number | null }): ValueAction[] {
  const out: ValueAction[] = [
    act.open(`/tour-operators/${tenant.id}`, 'Open operator'),
    act.open(`/tour-operators/${tenant.id}/integrations`, "Operator's integrations"),
    act.filter(subscriptionsHref([], tenant.id), 'Subscriptions of this operator'),
  ];
  if (tenant.parent_id) {
    out.push(act.open(`/tour-operators/${tenant.parent_id}`, 'Open the parent operator'));
  }
  return out;
}

export function restechActions(ctx: SubscriptionCtx): ValueAction[] {
  const t = tenantOf(ctx);
  const out: ValueAction[] = [
    act.filter(subscriptionsHref([cond('restech', ctx.restech)], t), 'Subscriptions of this booking system'),
    act.filter(tourOperatorsHref([cond('restech', ctx.restech)]), 'Operators on this booking system'),
    act.filter(
      apiRequestsHref([cond('integration', ctx.restech)], { tenant: t }),
      'API requests of this integration (7 days)',
    ),
  ];
  if (t != null) out.push(act.open(`/tour-operators/${t}/integrations`, "Operator's integrations"));
  return out;
}

/** The account slug — empty string is the default account, which has nothing
 *  to filter by (tenant + restech already pin it down). */
export function accountActions(account: string, ctx: SubscriptionCtx): ValueAction[] {
  const t = tenantOf(ctx);
  if (!account) {
    return [act.filter(subscriptionsHref([cond('restech', ctx.restech)], t), 'Subscriptions of this booking system')];
  }
  return [
    act.copy(account, 'Copy account slug'),
    act.filter(subscriptionsHref([cond('account', account)], t), 'Subscriptions of this account'),
    ...(t != null
      ? [act.filter(subscriptionsHrefForAccount(t, ctx.restech, account), 'This integration account')]
      : []),
  ];
}

export function typeActions(notificationType: string, ctx: SubscriptionCtx): ValueAction[] {
  return [
    act.filter(
      subscriptionsHref([cond('type', notificationType)], tenantOf(ctx)),
      `Subscriptions of type ${notificationType}`,
    ),
  ];
}

export function modeActions(mode: string, ctx: SubscriptionCtx): ValueAction[] {
  return [act.filter(subscriptionsHref([cond('mode', mode)], tenantOf(ctx)), `Subscriptions in "${mode}" mode`)];
}

export function statusActions(status: string, ctx: SubscriptionCtx): ValueAction[] {
  return [act.filter(subscriptionsHref([cond('status', status)], tenantOf(ctx)), `Subscriptions "${status}"`)];
}

export function subscriptionIdActions(id: number, ctx: SubscriptionCtx): ValueAction[] {
  return [
    act.copy(String(id), 'Copy subscription id'),
    act.filter(subscriptionsHref([cond('id', String(id), 'exact')], tenantOf(ctx)), 'This subscription in the grid'),
  ];
}

/** The vendor-side subscription id. The vendor back-office URL is not in the
 *  payload (Phase 8), so this stays a copy + grid filter. */
export function remoteIdActions(remoteId: string, ctx: SubscriptionCtx): ValueAction[] {
  return [
    act.copy(remoteId, 'Copy remote id'),
    act.filter(subscriptionsHref([cond('remote', remoteId, 'exact')], tenantOf(ctx)), 'Subscriptions by remote id'),
  ];
}

export function createdActions(iso: string, ctx: SubscriptionCtx): ValueAction[] {
  const t = tenantOf(ctx);
  return act.timeRanges(iso, (range) => subscriptionsHref([range], t));
}

/** The last delivery that arrived: the webhook log around that instant. */
export function lastReceivedActions(iso: string, ctx: SubscriptionCtx): ValueAction[] {
  const t = tenantOf(ctx);
  return [
    act.filter(
      apiRequestsHref([cond('integration', ctx.restech)], { tenant: t, window: createdAroundNode(iso) }),
      'Requests around the last delivery (±1 h)',
    ),
    act.filter(
      subscriptionsHref([cond('last_received', iso.slice(0, 10), 'exact')], t),
      'Subscriptions last heard from that day',
    ),
  ];
}

/** The last failure: the failed webhooks of this integration around it. */
export function lastErrorActions(iso: string | null | undefined, ctx: SubscriptionCtx): ValueAction[] {
  const t = tenantOf(ctx);
  const nodes = [cond('integration', ctx.restech), cond('errors', 'yes', 'exact')];
  return [
    act.filter(
      apiRequestsHref(nodes, { tenant: t, ...(iso ? { window: createdAroundNode(iso) } : {}) }),
      iso ? 'Failed requests around this error (±1 h)' : 'Failed requests of this integration (7 days)',
    ),
  ];
}

// ---- Bound API credential -------------------------------------------------

export function credentialActions(cred: { id: number; name: string }): ValueAction[] {
  return [
    act.open(`/api-credentials/${cred.id}`, 'Open API credential'),
    act.copy(cred.name, 'Copy credential name'),
  ];
}

export function credentialPrefixActions(prefix: string): ValueAction[] {
  return [
    act.copy(prefix, 'Copy key prefix'),
    act.filter(apiCredentialsHref([cond('prefix', prefix)]), 'Credentials with this key prefix'),
  ];
}

export function credentialFingerprintActions(fingerprint: string, ctx: SubscriptionCtx): ValueAction[] {
  return [
    act.copy(fingerprint, 'Copy fingerprint'),
    act.filter(apiCredentialsHref([cond('fingerprint', fingerprint)]), 'Credentials with this fingerprint'),
    act.filter(
      subscriptionsHref([cond('credential', fingerprint, 'exact')], tenantOf(ctx)),
      'Subscriptions bound to this credential',
    ),
  ];
}

export function credentialStatusActions(status: string): ValueAction[] {
  return [act.filter(apiCredentialsHref([cond('status', status)]), `Credentials "${status}"`)];
}
